import type { Changelog, ChangelogTranslation } from "@productlane/zero"
import { ownChangelogCover } from "@productlane/lib/src/changelog-cover"
import { formatRelativeTime } from "@productlane/lib/src/date"

import { sortChangelogsForNav } from "./changelog-order"

export type OverviewChangelog = Changelog & {
	translations?: readonly ChangelogTranslation[]
}

export interface ChangelogOverview<T> {
	drafts: T[]
	latest: T[]
	older: T[]
}

export const LATEST_POST_COUNT = 5

const ONE_MINUTE = 60 * 1000

export const changelogTimestamp = (
	changelog: Pick<Changelog, "date" | "createdAt">,
) => changelog.date || changelog.createdAt

export const buildChangelogOverview = <T extends OverviewChangelog>(
	changelogs: readonly T[],
	latestCount = LATEST_POST_COUNT,
): ChangelogOverview<T> => {
	const sorted = sortChangelogsForNav(
		changelogs.filter(
			(changelog) => !changelog.archived && !changelog.isDeleted,
		),
	)
	const drafts = sorted.filter((changelog) => !changelog.published)
	const published = sorted.filter((changelog) => changelog.published)

	return {
		drafts,
		latest: published.slice(0, latestCount),
		older: published.slice(latestCount),
	}
}

// Translation rows carry their own edit time, and a non-default-language save
// only touches those, so the newest of the two is the real edit time.
export const changelogEditedLabel = (
	changelog: Pick<OverviewChangelog, "updatedAt" | "translations">,
	now = Date.now(),
) => {
	const lastEditedAt = Math.max(
		changelog.updatedAt,
		...(changelog.translations ?? []).map((t) => t.updatedAt),
	)
	return now - lastEditedAt < ONE_MINUTE
		? "Edited just now"
		: `Edited ${formatRelativeTime(new Date(lastEditedAt), true)}`
}

const textOf = (node: unknown): string => {
	if (!node || typeof node !== "object") return ""
	const obj = node as { type?: unknown; text?: unknown; content?: unknown }
	if (obj.type === "text" && typeof obj.text === "string") return obj.text
	return Array.isArray(obj.content) ? obj.content.map(textOf).join("") : ""
}

// The first paragraph is the lede every changelog opens with; headings and
// lists further down read as fragments out of context.
export const changelogExcerpt = (notes: unknown) => {
	const blocks = (notes as { content?: unknown } | null)?.content
	if (!Array.isArray(blocks)) return ""
	for (const block of blocks) {
		if ((block as { type?: unknown })?.type !== "paragraph") continue
		const text = textOf(block).replace(/\s+/g, " ").trim()
		if (text) return text
	}
	return ""
}

export const localizedChangelog = (
	changelog: OverviewChangelog,
	languageCode: string,
) => {
	const translation = changelog.translations?.find(
		(t) => t.language === languageCode,
	)
	return {
		title: translation?.title || changelog.title || "New changelog",
		notes: translation?.notes ?? changelog.notes,
		imageUrl: ownChangelogCover(translation?.imageUrl, changelog.imageUrl),
		imageBlurhash: translation?.imageUrl
			? (translation.imageBlurhash ?? null)
			: (changelog.imageBlurhash ?? null),
	}
}
